import {
  BIG_CARDS_MIN_RANK,
  type BridgeCard,
  type BridgeSeat,
  RUMMY_TARGET,
  type RummyAction,
  type RummyGame,
  type RummyResult,
  type RummyRobotLevel,
  type RummyTotals,
  SINGLE_PLAYER,
  addRummyHand,
  layOffOnto,
  newRummyGame,
  rummyChoose,
  rummyDiscard,
  rummyDiscardable,
  rummyDraw,
  rummyGameWinner,
  rummyLayOff,
  rummyMeld,
  rummyResult,
  rummyViewFor,
  sameCard,
  seededRandom,
  validateMeld,
  wrapFromOf,
} from '@chessmate/game-engine';
import { create } from 'zustand';
import { seatPan } from '@chessmate/audio';
import { rememberBigCards, resolveBigCards } from '../lib/big-cards.js';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';
import { cueDeal, cueRoundOrGame, cueTurn } from './sfx-cues.js';
import { chatGameEnd, chatRound, chatterNewGame, robotsOf } from './table-chatter.js';

/**
 * The Rummy store (single device: you in South, robots in the other seats). Its own store, like Hearts' and Crazy Eights', because
 * nothing here is FEN-shaped. Robots decide from `rummyViewFor` only.
 *
 * A game is a run of hands; the winner of a hand scores the value of the cards left in the others' hands and the first to 100 wins
 * the game. Points are only a score: nothing is staked.
 *
 * Turn flow for the human: draw (stock or discard pile), then any number of melds and lay-offs, then discard. The cards to meld, lay
 * off or discard are the ones in `selected`, so every action is validated by the engine and the screen only offers what is legal.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';
export type PlayerCount = 2 | 3 | 4;
export const TABLE_SEATS: Readonly<Record<PlayerCount, readonly BridgeSeat[]>> = {
  2: ['N', 'S'],
  3: ['N', 'S', 'W'],
  4: ['N', 'E', 'S', 'W'],
};

/** Pause before a robot acts so a kid can follow the table. Zero in tests and with `?engine=fake`. */
const ROBOT_DELAY_MS = 700;

export interface RummyState {
  game: RummyGame | null;
  result: RummyResult | null;
  /** Hands dealt so far in this game, counting the current one (0 = no game). */
  handNumber: number;
  totals: RummyTotals;
  /** The score that wins the game: `RUMMY_TARGET`, or `?target=` when the page was opened with one (tests and audits). */
  target: number;
  gameWinner: BridgeSeat | null;
  players: PlayerCount;
  strength: RummyRobotLevel;
  fourColour: boolean;
  /** Big Cards: the 28-card deck of 8 to Ace (smaller hands). Off by default. */
  bigCards: boolean;
  /** Runs wrap around the ring of ranks (Q-K-A-8, or K-A-2 on the full deck). `null` follows the deck: on with Big Cards, off otherwise. Only offered on screen with Big Cards (on the 13-rank ring K-A-2 would just confuse). */
  wrap: boolean | null;
  /** Teaching aid, off by default: show what the clever robot would do. */
  assist: boolean;
  seed: number | null;
  query: AppQuery | null;
  /** The cards of the human's hand picked for a meld, a lay-off or the discard. */
  selected: BridgeCard[];
  start(query: AppQuery, seed?: number): void;
  nextHand(): void;
  newGame(): void;
  stop(): void;
  /** Tap a card in your hand to pick it or put it back. Only possible after you have drawn. */
  toggle(card: BridgeCard): void;
  draw(from: 'stock' | 'discard'): void;
  /** Lay the picked cards down as a set or a run. */
  meld(): void;
  /** Add the one picked card to the meld on the table with this id. */
  layOff(meldId: number): void;
  /** Discard the one picked card and end the turn. */
  discard(): void;
  setStrength(strength: RummyRobotLevel): void;
  setFourColour(on: boolean): void;
  setAssist(on: boolean): void;
  /** Switch the deck; restarts the game from the same seed. */
  setBigCards(on: boolean): void;
  /** Switch wrap-around runs on or off for the next deal (restarts the game from the same seed). */
  setWrap(on: boolean): void;
  setPlayers(players: PlayerCount): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each hand of a game has its own seed derived from the game's, so a seeded game replays exactly. */
export const handSeed = (seed: number, handIndex: number): number => (seed + Math.imul(handIndex, 7919)) >>> 0;

/** Apply one action, taken from a view of the player to move (a robot's choice), to the game. */
function applyAction(game: RummyGame, action: RummyAction): RummyGame {
  if (action.type === 'draw') return rummyDraw(game, action.from);
  if (action.type === 'meld') return rummyMeld(game, action.cards);
  if (action.type === 'layoff') return rummyLayOff(game, action.card, action.meldId);
  return rummyDiscard(game, action.card);
}

export const useRummyStore = create<RummyState>((set, get) => {
  /** Take a new game state; when it ends the hand, score it, and when that ends the game, say so. */
  const advance = (game: RummyGame) => {
    const before = get().game;
    const result = rummyResult(game);
    if (!result) {
      cueTurn(before && before.phase !== 'done' ? before.turn : null, game.phase !== 'done' ? game.turn : null, HUMAN_SEAT);
      // Whatever was picked is no longer in the hand (melded or discarded) or it is someone else's turn: keep only what is still held.
      const mine = game.hands[HUMAN_SEAT] ?? [];
      set({ game, selected: get().selected.filter((c) => game.turn === HUMAN_SEAT && game.phase === 'play' && mine.some((h) => sameCard(h, c))) });
      return;
    }
    const totals = addRummyHand(get().totals, result);
    const winner = rummyGameWinner(totals, get().target);
    set({ game, result, totals, gameWinner: winner, selected: [] });
    // The game-ending hand gets win/lose; any other hand the stinger.
    cueRoundOrGame((winner ?? result.winner) === HUMAN_SEAT ? 'won' : 'lost', winner !== null);
    // Table talk (public: who went out, who won the game).
    const robots = robotsOf(TABLE_SEATS[get().players]);
    if (winner) chatGameEnd([winner], robots);
    else chatRound(robots.includes(result.winner) ? result.winner : null, robots.find((r) => r !== result.winner) ?? null);
  };

  /** If a robot is to act, let it, after a short pause; stops at the human's turn or the end of the hand. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done' || game.controllers[game.turn] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase === 'done' || current.controllers[current.turn] !== 'ai') return;
      const random = seededRandom(current.seed + current.discard.length * 31 + current.stock.length + current.melds.length * 977);
      const seat = current.turn;
      const action = rummyChoose(rummyViewFor(current, seat), strength, random);
      // A card that reaches the table is heard from the seat that played it (spec P2: seat panning).
      if (action.type === 'discard' || action.type === 'layoff') playSfx('card-play', seatPan(seat));
      advance(applyAction(current, action));
      driveRobots();
    }, delay);
  };

  const deal = (handIndex: number) => {
    clearTimer();
    const { seed, players, bigCards, wrap } = get();
    if (seed === null) return;
    const seats = TABLE_SEATS[players];
    const game = newRummyGame({ seed: handSeed(seed, handIndex), seats: [...seats], dealer: seats[handIndex % seats.length], controllers: SINGLE_PLAYER, minRank: bigCards ? BIG_CARDS_MIN_RANK : 2, ...(wrap === null ? {} : { wrap }) });
    set({ game, result: null, handNumber: handIndex + 1, selected: [] });
    cueDeal();
    cueTurn(null, game.turn, HUMAN_SEAT);
    driveRobots();
  };

  const begin = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    set({ seed, query, target: query.target ?? RUMMY_TARGET, totals: {}, gameWinner: null, result: null, handNumber: 0, selected: [] });
    deal(0);
  };

  const humanTurn = (g: RummyGame | null): g is RummyGame => !!g && g.phase !== 'done' && g.turn === HUMAN_SEAT;
  const humanPlay = (g: RummyGame | null): g is RummyGame => humanTurn(g) && g.phase === 'play';

  /** Run a human action; an illegal one (the engine throws) is simply ignored, so the screen can never wedge the game. */
  const act = (g: RummyGame, fn: (game: RummyGame) => RummyGame) => {
    let next: RummyGame;
    try {
      next = fn(g);
    } catch {
      return;
    }
    advance(next);
    driveRobots();
  };

  return {
    game: null,
    result: null,
    handNumber: 0,
    totals: {},
    target: RUMMY_TARGET,
    gameWinner: null,
    players: 3,
    strength: 'easy',
    fourColour: false,
    bigCards: false,
    wrap: null,
    assist: false,
    seed: null,
    query: null,
    selected: [],
    start: (query, seed) => {
      setAmbientMood('cards');
      set({ bigCards: resolveBigCards('rummy', query.bigCards), wrap: null });
      begin(query, seed ?? query.deal ?? freshSeed());
    },
    nextHand: () => {
      const { result, gameWinner: winner, handNumber } = get();
      if (!result || winner) return;
      deal(handNumber);
    },
    newGame: () => {
      const { query } = get();
      if (query) begin(query, query.deal ?? freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null, handNumber: 0, totals: {}, target: RUMMY_TARGET, gameWinner: null, selected: [] });
    },
    toggle: (card) => {
      const { game, selected } = get();
      if (!humanPlay(game) || !(game.hands[HUMAN_SEAT] ?? []).some((h) => sameCard(h, card))) return;
      set({ selected: selected.some((c) => sameCard(c, card)) ? selected.filter((c) => !sameCard(c, card)) : [...selected, card] });
    },
    draw: (from) => {
      const { game } = get();
      if (!humanTurn(game) || game.phase !== 'draw') return;
      act(game, (g) => rummyDraw(g, from));
    },
    meld: () => {
      const { game, selected } = get();
      if (!humanPlay(game) || !validateMeld(selected, game.aceHigh, wrapFromOf(game))) return;
      act(game, (g) => rummyMeld(g, selected));
    },
    layOff: (meldId) => {
      const { game, selected } = get();
      const meld = game?.melds.find((m) => m.id === meldId);
      if (!humanPlay(game) || !meld || selected.length !== 1 || !layOffOnto(meld, selected[0] as BridgeCard, game.aceHigh, wrapFromOf(game))) return;
      // The card reaches the table, so it is heard.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      act(game, (g) => rummyLayOff(g, selected[0] as BridgeCard, meldId));
    },
    discard: () => {
      const { game, selected } = get();
      if (!humanPlay(game) || selected.length !== 1) return;
      const card = selected[0] as BridgeCard;
      if (!rummyDiscardable(game).some((c) => sameCard(c, card))) return;
      // Only a card that reaches the table is heard; an illegal tap is silent.
      playSfx('card-play', seatPan(HUMAN_SEAT));
      act(game, (g) => rummyDiscard(g, card));
    },
    setStrength: (strength) => set({ strength }),
    setFourColour: (fourColour) => set({ fourColour }),
    setAssist: (assist) => set({ assist }),
    setBigCards: (bigCards) => {
      rememberBigCards('rummy', bigCards);
      set({ bigCards, wrap: null }); // back to the deck's own default
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
    setWrap: (wrap) => {
      set({ wrap });
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
    setPlayers: (players) => {
      set({ players });
      const { query } = get();
      if (query && get().seed !== null) begin(query, get().seed as number);
    },
  };
});

export { RUMMY_TARGET };
